import { t } from 'i18next';
import { Info, Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { LoadingSpinner } from '@/components/ui/spinner';
import { piecesHooks } from '@/features/pieces/lib/pieces-hook';
import { ADVANCED_PIECE_NAMES } from '@activepieces/shared';

import { CatalogCategory } from '../lib/catalog-categories';
import { triggerStrategyLabel } from '../lib/catalog-labels';
import { sortByDisplayName } from '../lib/catalog-view';

import {
  CatalogBreadcrumb,
  PieceCatalogDetailHeader,
} from './piece-catalog-detail-header';

// Most pieces have a handful of steps: a reading-width column keeps a sparse
// page from looking unfinished on a wide admin screen.
const DETAIL_COLUMN_CLASS = 'mx-auto flex w-full max-w-6xl flex-col gap-8';

type PieceCatalogDetailProps = {
  pieceName: string;
  catalogPath: string;
  categoryPath: (category: CatalogCategory) => string;
};

type CatalogSectionItem = {
  name: string;
  displayName: string;
  description: string;
  badge?: string;
};

type CatalogSectionProps = {
  title: string;
  items: CatalogSectionItem[];
};

const CapabilityNote = ({ children }: { children: string }) => (
  <p className="flex items-center gap-2 rounded-md bg-muted/50 px-4 py-3 text-sm text-muted-foreground">
    <Info className="size-4 shrink-0" />
    {children}
  </p>
);

// The grid cards only carry a "Business" badge; the detail page has room to
// spell out what it means for a client.
const BusinessPlanNotice = () => (
  <p className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
    <Lock className="size-4 shrink-0" />
    {t(
      'Business plan only. Clients on another plan cannot use this app in their flows.',
    )}
  </p>
);

const CatalogSectionCard = ({ item }: { item: CatalogSectionItem }) => (
  <li className="flex h-full flex-col gap-1.5 rounded-lg border bg-background p-4">
    <p className="font-medium">{item.displayName}</p>
    <p className="text-sm text-muted-foreground">{item.description}</p>
    {item.badge && (
      <Badge variant="outline" className="mt-auto self-start">
        {item.badge}
      </Badge>
    )}
  </li>
);

const CatalogSection = ({ title, items }: CatalogSectionProps) => (
  <section className="flex flex-col gap-3">
    <h2 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
      {title}
      <Badge variant="secondary" className="px-2 py-0 tracking-normal">
        {items.length}
      </Badge>
    </h2>
    <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {items.map((item) => (
        <CatalogSectionCard key={item.name} item={item} />
      ))}
    </ul>
  </section>
);

export const PieceCatalogDetail = ({
  pieceName,
  catalogPath,
  categoryPath,
}: PieceCatalogDetailProps) => {
  const { i18n } = useTranslation();
  const { pieceModel, isLoading, isError } = piecesHooks.usePiece({
    name: pieceName,
  });

  if (isLoading) {
    return (
      <div className="flex h-[calc(100vh-4.5rem)] items-center justify-center">
        <LoadingSpinner size={50} />
      </div>
    );
  }
  if (isError || !pieceModel) {
    return (
      <div className={DETAIL_COLUMN_CLASS}>
        <CatalogBreadcrumb links={[{ label: t('Catalog'), to: catalogPath }]} />
        <p className="text-muted-foreground">
          {t('This app does not exist or could not be loaded')}
        </p>
      </div>
    );
  }

  const triggers = sortByDisplayName(
    Object.values(pieceModel.triggers),
    i18n.language,
  ).map((trigger) => ({
    name: trigger.name,
    displayName: trigger.displayName,
    description: trigger.description,
    badge: triggerStrategyLabel(trigger.type),
  }));
  const actions = sortByDisplayName(
    Object.values(pieceModel.actions),
    i18n.language,
  );

  return (
    <div className={DETAIL_COLUMN_CLASS}>
      <PieceCatalogDetailHeader
        piece={pieceModel}
        catalogPath={catalogPath}
        categoryPath={categoryPath}
      />
      {ADVANCED_PIECE_NAMES.has(pieceModel.name) && <BusinessPlanNotice />}
      {triggers.length === 0 && (
        <CapabilityNote>
          {t('This app cannot start a flow, it can only act in one.')}
        </CapabilityNote>
      )}
      {actions.length === 0 && (
        <CapabilityNote>
          {t('This app can only start a flow, it has no action.')}
        </CapabilityNote>
      )}
      {triggers.length > 0 && (
        <CatalogSection title={t('Triggers')} items={triggers} />
      )}
      {actions.length > 0 && (
        <CatalogSection title={t('Actions')} items={actions} />
      )}
    </div>
  );
};
